import React, { useContext, useEffect, useState } from 'react';
import { UserProfileContext } from '../../../context/userProfileContext';
import InputWrapper from '../../../components/inputWrapper/InputWrapper';
import { User } from '../../../interfaces/User';
import { camelCaseToText } from '../../../utils/camelCaseToText';
import Modules from '../../../utils/modules';
import { Insurance, InsuranceClosedLostReasons, Polywizz } from '../../../interfaces/Insurance';
import { formatDate } from '../../../utils/formatDate';
import { DebounceInput } from 'react-debounce-input';
import { displayNumericInputFieldsValue } from '../../../utils/displayNumericValues';
import Select from 'react-select';
import { getInsuranceStatusTrackers, getPolywizzByInsuranceId } from '../../../utils/api/insurance';
import StatusHistory from '../../../components/StatusHistory';
import { Form } from 'react-bootstrap';

const closedLost = 'closedLost';

const Details = () => {
    const { profileContextData, onEditRound, users, allUsers, getUserById } = useContext(UserProfileContext);
    const { client, modules } = profileContextData;
    const selectedModule = profileContextData.selectedModule;
    const allowedStatusesOptions = selectedModule && Object.values(selectedModule.allowedStatuses);
    const selectedModuleName = selectedModule?.data.moduleName;
    const repType = [Modules.pension, Modules.insurance].includes(selectedModuleName) ? 'agent' : 'rep';

    const [currentRound, setCurrentRound] = useState<Insurance>(profileContextData.currentRound as Insurance);
    const [repAgents, setRepAgents] = useState<User[]>(
        users.filter((rep: User) => rep.roles.includes('agent') && rep.agentModules?.includes('insurance')),
    );

    const [operators, setOperators] = useState<User[]>(
        users.filter((rep: User) => rep.roles.includes('operator') && rep.agentModules?.includes('insurance')),
    );

    const [polywizzClient, setPolywizzClient] = useState<Polywizz | null>(null);
    const [polywizzSpouse, setPolywizzSpouse] = useState<Polywizz | null>(null);

    useEffect(() => {
        setCurrentRound(profileContextData.currentRound as Insurance);
    }, [profileContextData.currentRound]);

    useEffect(() => {
        const fetchPolywizzData = async () => {
            if (!currentRound?._id) return;
            try {
                const { polywizzClient, polywizzSpouse } = await getPolywizzByInsuranceId(currentRound._id);
                setPolywizzClient(polywizzClient);
                setPolywizzSpouse(polywizzSpouse);
            } catch (error) {
                console.error('Error fetching polywizz data:', error);
            }
        };
        fetchPolywizzData();
    }, [currentRound?._id]);

    const handleStatusChange = (newStatus: string) => {
        if (newStatus !== closedLost) {
            onEditRound({ status: newStatus }, selectedModuleName, currentRound._id);
        }
        setCurrentRound({
            ...currentRound,
            status: newStatus,
            closedLostReason: undefined,
        });
    };

    const handleReasonChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newReason: InsuranceClosedLostReasons = event.target.value as InsuranceClosedLostReasons;
        onEditRound({ status: closedLost, closedLostReason: newReason }, selectedModuleName, currentRound._id, () =>
            setCurrentRound({
                ...currentRound,
                status: closedLost,
                closedLostReason: newReason,
            }),
        );
    };

    const onFieldsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        onEditRound({ [name]: value }, selectedModuleName, currentRound._id, () => setCurrentRound({ ...currentRound, [name]: value }));
    };

    useEffect(() => {
        setRepAgents(users.filter((rep: User) => rep.roles.includes('agent') && rep.agentModules?.includes('insurance')));
        setOperators(users.filter((rep: User) => rep.roles.includes('operator') && rep.agentModules?.includes('insurance')));
    }, [users]);

    const onAgentChanged = (agentId: string) => {
        const prevAgentId = currentRound[repType];
        setCurrentRound({ ...currentRound, [repType]: agentId });
        onEditRound({ [repType]: agentId }, selectedModuleName, currentRound._id, undefined, () =>
            setCurrentRound({ ...currentRound, [repType]: prevAgentId }),
        );
    };

    const onOneAgentForInsuranceAndPensionChecked = (checked: boolean) => {
        if (checked) {
            // Get client's active pension from profile context
            const agentIds = Array.from(new Set(modules.pensions?.pensions.map((pension: any) => pension.agent)));
            const agents = allUsers?.filter((user: User) => agentIds.includes(user._id));
            // Get the pension agent from users
            const pensionAgent = agents?.find(
                (agent: User) => agent.agentModules?.includes('insurance') && agent.agentModules?.includes('pension') && agent.isActive,
            );

            if (pensionAgent) {
                // Update both the agent and the checkbox
                setCurrentRound({
                    ...currentRound,
                    oneAgentForInsuranceAndPension: true,
                    agent: pensionAgent._id,
                });
                onEditRound(
                    {
                        oneAgentForInsuranceAndPension: true,
                        agent: pensionAgent._id,
                    },
                    selectedModuleName,
                    currentRound._id,
                );
            } else {
                // Pension agent doesn't meet criteria
                alert('The pension agent cannot handle both insurance and pension, or is not active.');
            }
        } else {
            // Just uncheck the box
            setCurrentRound({ ...currentRound, oneAgentForInsuranceAndPension: false });
            onEditRound({ oneAgentForInsuranceAndPension: false }, selectedModuleName, currentRound._id);
        }
    };

    return (
        <div className="detailed-tab">
            <div className="select-area" style={{ maxWidth: 'none' }}>
                {client && currentRound ? (
                    <div className="row">
                        <div className="col-md-3">
                            {allowedStatusesOptions && allowedStatusesOptions.length > 0 ? (
                                <InputWrapper status={{ text: '' }} label="Status" wrapperClass="input-field select-field">
                                    {/* <select value={currentRound.status || ''} className="form-select" name="status" onChange={handleStatusChange}>
                                        <option disabled value={''}>
                                            Select status
                                        </option>
                                        {allowedStatusesOptions.map((option: any, index: number) => (
                                            <option key={index} value={option}>
                                                {camelCaseToText(option)}
                                            </option>
                                        ))}
                                    </select> */}
                                    <Select
                                        options={allowedStatusesOptions.map((option: any, index: number) => ({
                                            label: camelCaseToText(option),
                                            value: option,
                                        }))}
                                        onChange={(item: any) => handleStatusChange(item?.value || '')}
                                        value={{ label: camelCaseToText(currentRound.status), value: currentRound.status }}
                                        className="d-flex flex-wrap"
                                        styles={{
                                            menu: (base) => ({
                                                ...base,
                                                width: '100%',
                                                // minWidth: '300px',
                                            }),
                                            control: (base) => ({
                                                ...base,
                                                width: '100%',
                                            }),
                                        }}
                                    />
                                </InputWrapper>
                            ) : null}
                            {
                                // If the close is lost, present the reason for it
                                currentRound.status === closedLost && (
                                    <InputWrapper
                                        {...(!currentRound.closedLostReason && {
                                            status: { text: 'Required field' },
                                        })}
                                        label="Close lost reason"
                                        wrapperClass="input-field select-field"
                                    >
                                        <select
                                            value={currentRound.closedLostReason || ''}
                                            className="form-select"
                                            name="reason"
                                            onChange={handleReasonChange}
                                        >
                                            <option disabled value={''}>
                                                Select reason
                                            </option>
                                            {Object.values(InsuranceClosedLostReasons).map((option: any, index: number) => (
                                                <option key={index} value={option}>
                                                    {camelCaseToText(option)}
                                                </option>
                                            ))}
                                        </select>
                                    </InputWrapper>
                                )
                            }
                            {repAgents && repAgents.length > 0 ? (
                                <div className="input-field select-field" style={{ marginBottom: '1rem' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                        <label className="form-label" style={{ margin: 0, fontSize: '1.3em' }}>
                                            Agent
                                        </label>
                                        <Form.Check
                                            type="checkbox"
                                            label="Same as pension"
                                            checked={currentRound.oneAgentForInsuranceAndPension || false}
                                            onChange={(e) => onOneAgentForInsuranceAndPensionChecked(e.target.checked)}
                                            style={{ marginBottom: 0 }}
                                        />
                                    </div>
                                    <Select
                                        options={repAgents.map((option: any, index: number) => ({
                                            label: option.fullName,
                                            value: option._id,
                                        }))}
                                        onChange={(item: any) => onEditRound({ [repType]: item.value }, selectedModuleName, currentRound._id)}
                                        value={{
                                            label: getUserById((currentRound as Insurance).agent as string)?.fullName,
                                            value: (currentRound as Insurance).agent as string,
                                        }}
                                        className="d-flex flex-wrap"
                                        styles={{
                                            menu: (base) => ({
                                                ...base,
                                                width: '100%',
                                                // minWidth: '300px',
                                            }),
                                            control: (base) => ({
                                                ...base,
                                                width: '100%',
                                            }),
                                        }}
                                    />
                                </div>
                            ) : (
                                <div className="pension-status">No {repType} found.</div>
                            )}

                            {operators && operators.length > 0 ? (
                                <InputWrapper status={{ text: '' }} label={'Operator'} wrapperClass="input-field select-field">
                                    <select
                                        value={currentRound.operator}
                                        className="form-select"
                                        name="operator"
                                        onChange={(e: any) => {
                                            onEditRound({ operator: e.target.value }, selectedModuleName, currentRound._id, () =>
                                                setCurrentRound({
                                                    ...currentRound,
                                                    operator: e.target.value,
                                                }),
                                            );
                                        }}
                                    >
                                        <option disabled value="">
                                            Select {'operator'}{' '}
                                        </option>
                                        {operators.map((option: any, index: number) => (
                                            <option key={index} value={option._id}>
                                                {option.fullName}
                                            </option>
                                        ))}
                                    </select>
                                </InputWrapper>
                            ) : (
                                <div className="pension-status">No operator found.</div>
                            )}
                        </div>
                        <div className="col-md-4" style={{ marginLeft: '30px' }}>
                            <InputWrapper status={{ text: '' }} label="First Kit Signature Date" wrapperClass="input-field input-field">
                                <input
                                    value={formatDate(currentRound.firstKitSignatureDate, true, false, 'en-CA')}
                                    className="form-control"
                                    name="firstKitSignatureDate"
                                    type="date"
                                    onChange={onFieldsChange}
                                    // onChange={e => onEditRound({ "firstKitSignatureDate": e.target.value }, selectedModuleName, currentRound._id)}
                                />
                            </InputWrapper>
                            <InputWrapper status={{ text: '' }} label="Appendices Submission Date" wrapperClass="input-field input-field">
                                <input
                                    value={formatDate(currentRound.appendicesSubmissionDate, true, false, 'en-CA')}
                                    className="form-control"
                                    name="appendicesSubmissionDate"
                                    type="date"
                                    onChange={onFieldsChange}
                                />
                            </InputWrapper>
                            {/* <InputWrapper status={{ text: '' }} label="Saving Amount" wrapperClass="input-field input-field">
                                <DebounceInput
                                    value={displayNumericInputFieldsValue(currentRound.savingAmount)}
                                    className="form-control"
                                    name="savingAmount"
                                    type="number"
                                    min={0}
                                    max={100}
                                    debounceTimeout={1500}
                                    onChange={onFieldsChange}
                                />
                            </InputWrapper> */}
                            <InputWrapper status={{ text: '' }} label="SMS2010 Link" wrapperClass="input-field input-field">
                                <DebounceInput
                                    value={currentRound.sms2010Link}
                                    className="form-control"
                                    debounceTimeout={1500}
                                    name="sms2010Link"
                                    onChange={onFieldsChange}
                                />
                            </InputWrapper>
                        </div>

                        <div>
                            <b>Agent meeting date: </b>
                            <span>{formatDate((currentRound as Insurance).agentMeetingTime)}</span>
                        </div>
                        <div>
                            <b>Service meeting date: </b>
                            <span>
                                {(currentRound as Insurance).serviceMeeting?.eventStartTime ? (
                                    <>
                                        {formatDate((currentRound as Insurance).serviceMeeting!.eventStartTime)}
                                        {(currentRound as Insurance).serviceMeeting!.repId && (
                                            <span>
                                                {' '}
                                                with <b>{getUserById((currentRound as Insurance).serviceMeeting!.repId)?.fullName || 'Unknown'}</b>
                                            </span>
                                        )}
                                    </>
                                ) : (
                                    'Not scheduled'
                                )}
                            </span>
                        </div>
                    </div>
                ) : (
                    <div>Sorry, no data yet.</div>
                )}

                {client && currentRound && (
                    <StatusHistory
                        fetchStatusTrackers={getInsuranceStatusTrackers}
                        moduleId={currentRound._id}
                        moduleName="insurance"
                        polywizzClient={polywizzClient}
                        polywizzSpouse={polywizzSpouse}
                    />
                )}
            </div>
        </div>
    );
};

export default Details;
